import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface SystemSettingsMap {
  maintenance_mode: { enabled: boolean } | null;
  seo_config: Record<string, string> | null;
  pricing_config: Record<string, unknown> | null;
  subscriptions_enabled: boolean;
}

/**
 * Single query to load ALL system_settings at once.
 * Replaces 3 separate queries (maintenance, seo, pricing) with one batch.
 * Cached for 5 minutes, polled every 5 minutes for maintenance mode freshness.
 */
export function useSystemSettings() {
  return useQuery({
    queryKey: ["system-settings-batch"],
    queryFn: async (): Promise<SystemSettingsMap> => {
      const { data, error } = await supabase
        .from("system_settings")
        .select("setting_key, setting_value")
        .in("setting_key", ["maintenance_mode", "seo_config", "pricing_config", "subscriptions_enabled"]);

      if (error) {
        console.error("Error fetching system settings:", error);
        return { maintenance_mode: null, seo_config: null, pricing_config: null, subscriptions_enabled: true };
      }

      const map: SystemSettingsMap = {
        maintenance_mode: null,
        seo_config: null,
        pricing_config: null,
        subscriptions_enabled: true, // Default: enabled
      };

      for (const row of data || []) {
        if (row.setting_key === "maintenance_mode") {
          map.maintenance_mode = row.setting_value as { enabled: boolean } | null;
        } else if (row.setting_key === "seo_config") {
          map.seo_config = row.setting_value as Record<string, string> | null;
        } else if (row.setting_key === "pricing_config") {
          map.pricing_config = row.setting_value as Record<string, unknown> | null;
        } else if (row.setting_key === "subscriptions_enabled") {
          const val = row.setting_value as { enabled?: boolean } | null;
          map.subscriptions_enabled = val?.enabled !== false; // Default true
        }
      }

      return map;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30,
    refetchInterval: 1000 * 60 * 5, // Poll every 5 min (was 30 sec!)
    refetchOnWindowFocus: false,
  });
}
